import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';
const BRAND_YELLOW = '#FFD166';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await api.get(`/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setUserEmail(res.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setResetComplete(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 mx-auto" style={{ borderTopColor: BRAND_BLUE }}></div>
          <p className="mt-4 text-gray-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block px-6 py-3 text-white rounded-xl font-semibold transition hover:opacity-90"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            Request New Reset Link
          </Link>
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 mt-6 text-sm font-medium transition hover:opacity-80"
            style={{ color: BRAND_BLUE }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // Reset complete
  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
            <CheckCircle className="w-8 h-8" style={{ color: BRAND_BLUE }} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Complete</h2>
          <p className="text-gray-600 mb-6">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 text-white rounded-xl font-semibold transition hover:opacity-90"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12" style={{ backgroundColor: BRAND_BLUE }}>
        <div className="max-w-md text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 flex flex-col justify-center items-center gap-0.5">
              <div className="w-8 h-2 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
              <div className="w-10 h-2 rounded-full" style={{ backgroundColor: BRAND_ORANGE }}></div>
              <div className="w-12 h-2 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
            </div>
            <span className="text-white font-bold text-4xl tracking-wide">TRADOS</span>
          </div>
          <p className="text-white/80 text-lg">The Trusted Trade Platform</p>
          <p className="text-white/60 mt-4">
            Create a new secure password for your account.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 flex flex-col justify-center items-center gap-0.5">
              <div className="w-6 h-1.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
              <div className="w-7 h-1.5 rounded-full" style={{ backgroundColor: BRAND_ORANGE }}></div>
              <div className="w-8 h-1.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
            </div>
            <span className="font-bold text-2xl" style={{ color: BRAND_BLUE }}>TRADOS</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Create new password</h2>
          <p className="text-gray-600 mb-2">
            Enter a new password for <strong>{userEmail}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Make sure it's at least 6 characters long.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || password !== confirmPassword}
              className="w-full py-3 text-white rounded-xl font-semibold transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 mt-6 text-sm font-medium transition hover:opacity-80"
            style={{ color: BRAND_BLUE }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
